import React, { Component } from 'react'
import Layout from '../../components/Layout'
import UserToolbar from '../../components/UserToolbar'
import { withStyles } from '@material-ui/core/styles'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import RefreshIcon from '@material-ui/icons/Refresh'
import {
  Table,
  TableBody,
  TableHeader,
  TableHeaderColumn,
  TableRow,
  TableRowColumn,
} from 'material-ui/Table'
import { bookVectorization, contentVectorization } from '../../api'
import VetorizacaoNav from '../../components/VetorizacaoNav'
import { STATUS_CONFIG, STATUS_FILTERS, isProcessing, isCompleted, isFailed } from '../../constants/vectorization'

const POLL_INTERVAL_MS = 30000

const styles = (theme) => ({
  root: {
    padding: '16px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '12px',
  },
  filterBar: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  section: {
    marginBottom: '24px',
  },
  sectionTitle: {
    padding: '12px 16px',
    fontWeight: 600,
    fontSize: '1rem',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f5f5f5',
  },
  loadingRow: {
    display: 'flex',
    justifyContent: 'center',
    padding: '32px',
  },
  emptyRow: {
    textAlign: 'center',
    padding: '24px',
    color: 'rgba(0,0,0,0.54)',
  },
  lastRefresh: {
    fontSize: '0.75rem',
    color: 'rgba(0,0,0,0.54)',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorBox: {
    padding: '16px',
    backgroundColor: '#fff3f3',
    borderRadius: '4px',
    marginBottom: '16px',
  },
})

class VectorizationStatus extends Component {
  state = {
    loading: false,
    books: [],
    contents: [],
    diagnosticLogs: {},
    lastRefresh: null,
    error: null,
    filter: 'all',
  }

  componentDidMount() {
    this.fetchAll()
    this._interval = setInterval(this.fetchAll, POLL_INTERVAL_MS)
  }

  componentWillUnmount() {
    clearInterval(this._interval)
  }

  fetchAll = () => {
    this.setState({ loading: true })

    const booksPromise = bookVectorization.getBookVectorizations({
      page: 0,
      perPage: 50,
      order: { column: 'created_at', direction: 1 },
    })

    const contentsPromise = contentVectorization.getConteudos({
      page: 0,
      perPage: 50,
    })

    const lambdaNames = ['vectorize', 'conteudo-vectorize', 'conteudo-ingest']
    const logsPromises = lambdaNames.map((lambda) =>
      contentVectorization
        .getDiagnosticLogs(lambda, { minutos: 10, limite: 10 })
        .then((res) => ({ lambda, data: res.data }))
        .catch(() => ({ lambda, data: null })),
    )

    Promise.all([booksPromise, contentsPromise, ...logsPromises])
      .then(([booksRes, contentsRes, ...logsRes]) => {
        const booksData = (booksRes.data || {}).items || []
        const books = booksData.map((b) => ({
          _type: 'livro',
          _id: b.isbn || b.id,
          _title: b.book_name || b.titulo,
          _status: (b.processamento || {}).status_atual || (b.status || {}).status || b.status || '',
          _date: b.created_at,
          _extra: b.especialidade || '',
        }))

        const contentsData = (contentsRes.data || {}).items || []
        const contents = contentsData.map((c) => ({
          _type: 'conteudo',
          _id: c.content_id,
          _title: c.titulo,
          _status: c.status,
          _date: c.criado_em || c.created_at,
          _extra: c.tipo || '',
        }))

        const diagnosticLogs = logsRes.reduce((acc, item) => {
          acc[item.lambda] = item.data
          return acc
        }, {})

        this.setState({
          loading: false,
          books,
          contents,
          diagnosticLogs,
          lastRefresh: new Date(),
          error: null,
        })
      })
      .catch(() => {
        this.setState({
          loading: false,
          error: 'Não foi possível carregar os dados. O ambiente pode estar instável — tentando novamente em 30s.',
        })
      })
  }

  applyFilter = (items) => {
    const { filter } = this.state
    if (filter === 'all') return items
    if (filter === 'processing') return items.filter((i) => isProcessing(i._status))
    if (filter === 'completed') return items.filter((i) => isCompleted(i._status))
    if (filter === 'failed') return items.filter((i) => isFailed(i._status))
    return items
  }

  renderStatus = (status) => {
    const config = STATUS_CONFIG[status] || { label: status || '—', color: 'default' }
    return <Chip label={config.label} color={config.color} size="small" />
  }

  renderLogs = (lambdaName, label) => {
    const payload = this.state.diagnosticLogs[lambdaName]
    const entries = (payload && (payload.eventos || payload.logs || payload.items || payload.events)) || []

    return (
      <Paper className={this.props.classes.section} elevation={2}>
        <div className={this.props.classes.sectionTitle}>
          {label} ({entries.length})
        </div>
        <div style={{ padding: 16 }}>
          {entries.length ? (
            <pre
              style={{
                margin: 0,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 240,
                overflow: 'auto',
              }}
            >
              {JSON.stringify(entries.slice(0, 5), null, 2)}
            </pre>
          ) : (
            <Typography color="textSecondary">
              {'Nenhum log recente retornado para ' + lambdaName + '.'}
            </Typography>
          )}
        </div>
      </Paper>
    )
  }

  renderTable = (items, tipo) => {
    const { classes } = this.props

    if (!items.length) {
      return (
        <Typography className={classes.emptyRow}>
          Nenhum item {tipo === 'livro' ? 'de livro' : 'de conteúdo'} encontrado para este filtro.
        </Typography>
      )
    }

    return (
      <Table fixedHeader={false}>
        <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
          <TableRow>
            <TableHeaderColumn>{tipo === 'livro' ? 'Título / ISBN' : 'Título / Content ID'}</TableHeaderColumn>
            <TableHeaderColumn style={{ width: '130px' }}>
              {tipo === 'livro' ? 'Especialidade' : 'Tipo'}
            </TableHeaderColumn>
            <TableHeaderColumn style={{ width: '130px' }}>Status</TableHeaderColumn>
            <TableHeaderColumn style={{ width: '160px' }}>Data</TableHeaderColumn>
          </TableRow>
        </TableHeader>
        <TableBody displayRowCheckbox={false}>
          {items.map((item, idx) => (
            <TableRow key={item._id || idx}>
              <TableRowColumn>
                <div>
                  <div
                    title={item._title}
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {item._title || '--'}
                  </div>
                  <Typography variant="caption" color="textSecondary">
                    {item._id}
                  </Typography>
                </div>
              </TableRowColumn>
              <TableRowColumn style={{ width: '130px' }}>{item._extra || '--'}</TableRowColumn>
              <TableRowColumn style={{ width: '130px' }}>
                {this.renderStatus(item._status)}
              </TableRowColumn>
              <TableRowColumn style={{ width: '160px' }}>
                {item._date ? new Date(item._date).toLocaleString('pt-BR') : '--'}
              </TableRowColumn>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    )
  }

  render() {
    const { classes } = this.props
    const { loading, books, contents, lastRefresh, error, filter } = this.state

    const filteredBooks = this.applyFilter(books)
    const filteredContents = this.applyFilter(contents)

    return (
      <Layout toolBarRight={UserToolbar}>
        <div className={classes.root}>
          <VetorizacaoNav active="status" />

          {/* Header */}
          <div className={classes.header}>
            <Typography variant="h6">Status / Processamento</Typography>
            <div className={classes.refreshBtn}>
              {lastRefresh && (
                <Typography className={classes.lastRefresh}>
                  Atualizado: {lastRefresh.toLocaleTimeString('pt-BR')} · auto-refresh 30s
                </Typography>
              )}
              <Button
                variant="outlined"
                size="small"
                onClick={this.fetchAll}
                disabled={loading}
              >
                <RefreshIcon style={{ marginRight: 6, fontSize: 18 }} />
                {loading ? 'Atualizando...' : 'Atualizar'}
              </Button>
            </div>
          </div>

          {/* Filtros */}
          <div className={classes.filterBar}>
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.key}
                size="small"
                variant={filter === f.key ? 'contained' : 'outlined'}
                color={filter === f.key ? 'primary' : 'default'}
                onClick={() => this.setState({ filter: f.key })}
              >
                {f.label}
              </Button>
            ))}
          </div>

          {/* Erro */}
          {error && (books.length > 0 || contents.length > 0) ? (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fff8e1',
              border: '1px solid #ffe082',
              borderRadius: '4px',
              marginBottom: '16px',
            }}>
              <Typography style={{ color: '#f57f17' }}>
                {error}
                {lastRefresh && ` Exibindo dados de ${lastRefresh.toLocaleTimeString('pt-BR')}.`}
              </Typography>
            </div>
          ) : error ? (
            <div className={classes.errorBox}>
              <Typography color="error">{error}</Typography>
            </div>
          ) : null}

          {/* Loading inicial */}
          {loading && !books.length && !contents.length && (
            <div className={classes.loadingRow}>
              <CircularProgress />
            </div>
          )}

          {/* Seção: Livros */}
          <Paper className={classes.section} elevation={2}>
            <div className={classes.sectionTitle}>
              Livros ({filteredBooks.length})
              {loading && (
                <CircularProgress size={14} style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              )}
            </div>
            {this.renderTable(filteredBooks, 'livro')}
          </Paper>

          {/* Seção: Conteúdos */}
          <Paper className={classes.section} elevation={2}>
            <div className={classes.sectionTitle}>
              Conteúdos ({filteredContents.length})
              {loading && (
                <CircularProgress size={14} style={{ marginLeft: 8, verticalAlign: 'middle' }} />
              )}
            </div>
            {this.renderTable(filteredContents, 'conteudo')}
          </Paper>

          {this.renderLogs('vectorize', 'Logs Lambda vectorize')}
          {this.renderLogs('conteudo-vectorize', 'Logs Lambda conteudo-vectorize')}
          {this.renderLogs('conteudo-ingest', 'Logs Lambda conteudo-ingest')}
        </div>
      </Layout>
    )
  }
}

export default withStyles(styles)(VectorizationStatus)



// WEBPACK FOOTER //
// ./src/containers/VectorizationStatus/index.js